export const BUSINESS_TYPES_OPTIONS = [
  { label: "Personal", value: "PERSONAL" },
  { label: "Incorporated", value: "BUSINESS" },
];

// Business lifecycle status (mirrors backend BusinessStatus enum). A null/absent
// status is treated as ACTIVE. RESTRICTED / PENDING_DELETION block backend writes.
//
// `shortLabel` is what the pill actually prints — it has to survive a ~180px
// sidebar column on one line. `label` stays the full phrase and is carried in
// the tooltip / accessible name so nothing is lost. Tones mirror the house
// Alert / BusinessStatusBanner scale (bg-50 + text-700 + ring-200) so a status
// pill reads as a quiet marker next to the business name, not an alarm.
export const BUSINESS_STATUS = {
  ACTIVE: {
    value: "ACTIVE",
    label: "Active",
    shortLabel: "Active",
    badgeClass: "bg-green-50 text-green-700 ring-green-600/20",
  },
  INACTIVE_WARNED: {
    value: "INACTIVE_WARNED",
    label: "Inactive",
    shortLabel: "Inactive",
    badgeClass: "bg-amber-50 text-amber-700 ring-amber-600/20",
  },
  RESTRICTED: {
    value: "RESTRICTED",
    label: "Restricted",
    shortLabel: "Restricted",
    badgeClass: "bg-orange-50 text-orange-700 ring-orange-600/20",
  },
  PENDING_DELETION: {
    value: "PENDING_DELETION",
    label: "Scheduled for deletion",
    shortLabel: "Will be deleted",
    badgeClass: "bg-red-50 text-red-700 ring-red-600/20",
  },
};

export const getBusinessStatus = (business) =>
  (business && business.status) || "ACTIVE";

// A PENDING_DELETION business has two lineages, told apart by `restrictedAt`:
//  - free-limit lineage (restrictedAt set)  -> writes blocked; recover via premium.
//  - inactivity lineage (no restrictedAt)    -> writes ALLOWED; a transaction
//    recovers it (matches the relaxed backend guard). See getBusinessLifecycle.
export const isFreeLimitLineage = (business) =>
  Boolean(business && business.restrictedAt);

// Writes are blocked only where the backend guard blocks them: RESTRICTED, or a
// free-limit PENDING_DELETION. Inactivity-warned and inactivity-PENDING stay
// writable so the user can record a transaction and recover.
export const isBusinessWriteBlocked = (business) => {
  const status = getBusinessStatus(business);
  if (status === "RESTRICTED") return true;
  if (status === "PENDING_DELETION") return isFreeLimitLineage(business);
  return false;
};

// A RESTRICTED / PENDING_DELETION business must never be promoted to the
// account's main business — it would then be auto-selected app-wide and the
// user would land on a read-only workspace.
export const isBusinessMainBlocked = (business) => {
  const status = getBusinessStatus(business);
  return status === "RESTRICTED" || status === "PENDING_DELETION";
};

// A non-expired subscription to a paid package. `User.membership` is a
// denormalized flag that lags behind checkout/expiry, so the backend sweep
// (`is_owner_free`) ignores it and looks at live PackageSubscription rows only.
// Mirror that here. The web `subscriptionsByUserExpiredAt` query already
// filters `expiredAt > now`, but re-check it so a stale cached list can't keep
// an expired plan alive.
export const hasLivePaidSubscription = (subscriptions) =>
  (subscriptions || []).some((sub) => {
    if (!sub) return false;
    const expiresAt = sub.expiredAt ? new Date(sub.expiredAt) : null;
    if (!expiresAt || Number.isNaN(expiresAt.getTime())) return false;
    if (expiresAt.getTime() <= Date.now()) return false;
    const tier = sub.package?.membershipType || sub.membershipType;
    return Boolean(tier) && tier !== "FREE";
  });

// Membership gate. Only the FREE tier is capped; PREMIUM / PRIORITY /
// CONSULTANT are exempt.
//
// `subscriptions` is the caller's loaded PackageSubscription list. When it is
// passed (an array — including an empty one, which means "loaded, none live")
// it is the sole truth, matching the backend. Callers that cannot load it fall
// back to the denormalized `user.membership`, and a membership that has not
// loaded yet is treated as FREE (conservative), matching mobile.
export const isFreeMember = (user, subscriptions) => {
  if (Array.isArray(subscriptions)) return !hasLivePaidSubscription(subscriptions);
  return (user?.membership || "FREE") === "FREE";
};

// Free accounts may keep only ONE business that still counts against the cap.
// Matches the backend sweep's `restrict_extra_businesses`, which ignores
// deleted, PENDING_DELETION *and* RESTRICTED businesses — a restricted business
// is already write-blocked, so counting it here would block a create the server
// would happily accept.
export const countLiveBusinesses = (businesses) =>
  (businesses || []).filter((b) => {
    if (b?.deletedAt) return false;
    const status = getBusinessStatus(b);
    return status !== "PENDING_DELETION" && status !== "RESTRICTED";
  }).length;

// A free member at/over the 1-business limit may not create more. Premium
// members and users with no live business (onboarding) are never blocked. The
// backend does not enforce this at create time (the daily lifecycle sweep only
// restricts extras after the fact); this is the front-end hard gate.
export const isFreeBusinessCreateBlocked = (user, businesses, subscriptions) =>
  isFreeMember(user, subscriptions) && countLiveBusinesses(businesses) >= 1;

// Ordering for a business picker: the business you're currently in comes first,
// then healthy ones, then the degraded ones the user can barely use (write-
// blocked last). Ties break on name so the list stays stable and scannable.
const BUSINESS_STATUS_RANK = {
  ACTIVE: 0,
  INACTIVE_WARNED: 1,
  RESTRICTED: 2,
  PENDING_DELETION: 3,
};

export const compareBusinessesForSwitch =
  (mainBusinessID) => (a, b) => {
    const aMain = Boolean(mainBusinessID) && a?.id === mainBusinessID;
    const bMain = Boolean(mainBusinessID) && b?.id === mainBusinessID;
    if (aMain !== bMain) return aMain ? -1 : 1;

    const aRank = BUSINESS_STATUS_RANK[getBusinessStatus(a)] ?? 0;
    const bRank = BUSINESS_STATUS_RANK[getBusinessStatus(b)] ?? 0;
    if (aRank !== bRank) return aRank - bRank;

    return (a?.name || "").localeCompare(b?.name || "", undefined, {
      sensitivity: "base",
    });
  };

export const sortBusinessesForSwitch = (businesses, mainBusinessID) =>
  [...(businesses || [])].sort(compareBusinessesForSwitch(mainBusinessID));

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const addDays = (date, days) => new Date(date.getTime() + days * MS_PER_DAY);

// Days from now until `iso` (may be negative/past). Null-safe.
const daysUntil = (iso) => {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  return Math.max(0, Math.ceil((then.getTime() - Date.now()) / MS_PER_DAY));
};

// Mirror of the backend inactivity cadence (warn at 90d, soft-delete at 120d):
// a warned business has ~30 days before it is deactivated.
const INACTIVITY_WARN_TO_SOFT_DELETE_DAYS = 30;

// Hard-delete windows in `businessLifecycleSweep`, measured from `deletedAt`
// (the SOFT-delete stamp, always in the past). Which one applies is told by the
// lineage that produced the PENDING_DELETION:
//   inactivity normal (inactivityWarnedAt set, no restrictedAt) -> 60 days
//   long-dormant >1yr (neither stamp set)                       -> 30 days
//   free-limit / Track B (restrictedAt set)                     -> 30 days
const PURGE_DAYS_INACTIVITY = 60;
const PURGE_DAYS_DEFAULT = 30;

const getPurgeDays = (business) =>
  business?.inactivityWarnedAt && !business?.restrictedAt
    ? PURGE_DAYS_INACTIVITY
    : PURGE_DAYS_DEFAULT;

// Single source of truth for how a lifecycle state is presented and acted on.
// Returns null for a healthy (ACTIVE / absent) business. `ctaKind`:
//   record   -> open the add-transaction flow (keep it active / recover)
//   subscribe-> go to /subscription (lift a free-limit restriction)
export const getBusinessLifecycle = (business) => {
  const status = getBusinessStatus(business);
  if (!business || status === "ACTIVE") return null;

  const blocked = isBusinessWriteBlocked(business);
  const freeLimit = isFreeLimitLineage(business);

  let severity; // 'warning' | 'restricted' | 'danger'
  let deadline = null; // ISO string of the next hard boundary, when known
  let ctaKind;

  if (status === "INACTIVE_WARNED") {
    severity = "warning";
    ctaKind = "record";
    if (business.inactivityWarnedAt) {
      const d = new Date(business.inactivityWarnedAt);
      if (!Number.isNaN(d.getTime())) {
        deadline = addDays(d, INACTIVITY_WARN_TO_SOFT_DELETE_DAYS).toISOString();
      }
    }
  } else if (status === "RESTRICTED") {
    severity = "restricted";
    ctaKind = "subscribe";
    deadline = business.graceEndsAt || null;
  } else {
    // PENDING_DELETION. `deletedAt` is when the soft delete happened, not the
    // purge date — the countdown runs from there to the hard delete.
    severity = "danger";
    ctaKind = freeLimit ? "subscribe" : "record";
    if (business.deletedAt) {
      const d = new Date(business.deletedAt);
      if (!Number.isNaN(d.getTime())) {
        deadline = addDays(d, getPurgeDays(business)).toISOString();
      }
    }
  }

  return {
    status,
    severity,
    blocked,
    isFreeLimit: freeLimit,
    isInactivityPending: status === "PENDING_DELETION" && !freeLimit,
    deadline,
    daysLeft: daysUntil(deadline),
    ctaKind,
  };
};

export const BUSINESS_TYPES = {
  PERSONAL: "Personal",
  BUSINESS: "Business",
};

export const BUSINESS_ENTITY_TYPES = [
  { label: "PT Orang Pribadi", value: "PTOP" },
  { label: "PT", value: "PT" },
  { label: "CV", value: "CV" },
  { label: "Koperasi", value: "COOPERATIVE" },
  { label: "Firma", value: "FIRM" },
  { label: "BUMDes", value: "BUMDES" },
  { label: "BUMDes Bersama", value: "BUMDESBER" },
  { label: "Yayasan", value: "FOUNDATION" },
  { label: "Lainnya", value: "OTHER" },
];

// Business sector (category code) — mirrors mobile BUSINESS_CATEGORIES. Value
// is the stored code ('001'..'011', '999'); drives sector-aware default
// account names at COA init.
export const BUSINESS_CATEGORIES = [
  { label: "Kuliner", value: "001" },
  { label: "Pakaian", value: "002" },
  { label: "Kesehatan dan kecantikan", value: "003" },
  { label: "Otomotif", value: "004" },
  { label: "Sembako", value: "005" },
  { label: "Alat Bahan Bangunan", value: "006" },
  { label: "Alat Elektronik", value: "007" },
  { label: "Kerajinan Tangan", value: "008" },
  { label: "Pendidikan", value: "009" },
  { label: "Jasa", value: "010" },
  { label: "Pulsa / PPOB", value: "011" },
  { label: "Lainnya", value: "999" },
];

export const GENDER_OPTIONS = [
  { label: "employees.gender.male", value: "MALE" },
  { label: "employees.gender.female", value: "FEMALE" },
];

export const ID_TYPE_OPTIONS = [
  { label: "employees.idType.idCard", value: "KTP" },
  { label: "employees.idType.passport", value: "PASSPORT" },
];

export const EMPLOYMENT_TYPE_OPTIONS = [
  { label: "employees.employmentType.permanent", value: "PERMANENT" },
  { label: "employees.employmentType.contract", value: "CONTRACT" },
  { label: "employees.employmentType.intern", value: "INTERN" },
  { label: "employees.employmentType.probation", value: "PROBATION" },
  { label: "employees.employmentType.daily", value: "DAILY_WORKER" },
];

export const EMPLOYMENT_STATUS_OPTIONS = [
  { label: "employees.employmentStatus.active", value: "ACTIVE" },
  { label: "employees.employmentStatus.resigned", value: "INACTIVE" },
  { label: "employees.employmentStatus.leave", value: "ON_LEAVE" },
  { label: "employees.employmentStatus.terminated", value: "TERMINATED" },
];

export const SALARY_TYPE_OPTIONS = [
  { label: "employees.salary.type.monthly", value: "MONTHLY" },
  { label: "employees.salary.type.weekly", value: "WEEKLY" },
  { label: "employees.salary.type.daily", value: "DAILY" },
  { label: "employees.salary.type.hourly", value: "HOURLY" },
];

export const PAYMENT_METHOD_OPTIONS = [
  { label: "employees.salary.method.bankTransfer", value: "BANK_TRANSFER" },
  { label: "employees.salary.method.cash", value: "CASH" },
  { label: "employees.salary.method.check", value: "CHECK" },
];

export const PAYMENT_PERIOD_OPTIONS = [
  { label: "employees.salary.period.monthly", value: "MONTHLY" },
  { label: "employees.salary.period.twiceMonthly", value: "TWICE_MONTHLY" },
  { label: "employees.salary.period.weekly", value: "WEEKLY" },
];

export const ALLOWANCE_TYPE_OPTIONS = [
  {
    value: "BPJS_KETENAGAKERJAAN_JKK",
    label: "employees.allowanceTypes.bpjs_jkk",
  },
  {
    value: "BPJS_KETENAGAKERJAAN_JKM",
    label: "employees.allowanceTypes.bpjs_jkm",
  },
  { value: "COST_OF_LIVING", label: "employees.allowanceTypes.costOfLiving" },
  { value: "REMOTE_AREA", label: "employees.allowanceTypes.remoteArea" },
  { value: "POSITION", label: "employees.allowanceTypes.position" },
  { value: "SKILL", label: "employees.allowanceTypes.skill" },
  { value: "ATTENDANCE", label: "employees.allowanceTypes.attendance" },
  { value: "FAMILY", label: "employees.allowanceTypes.family" },
  { value: "HEALTH", label: "employees.allowanceTypes.health" },
  { value: "PUNCTUALITY", label: "employees.allowanceTypes.punctuality" },
  { value: "PERFORMANCE", label: "employees.allowanceTypes.performance" },
  { value: "COMMUNICATION", label: "employees.allowanceTypes.communication" },
  { value: "OVERTIME", label: "employees.allowanceTypes.overtime" },
  { value: "LOYALTY", label: "employees.allowanceTypes.loyalty" },
  { value: "NATURA_FOOD", label: "employees.allowanceTypes.naturaFood" },
  {
    value: "CHILD_EDUCATION",
    label: "employees.allowanceTypes.childEducation",
  },
  {
    value: "REIMBURSEMENT_EDUCATION",
    label: "employees.allowanceTypes.reimbursementEducation",
  },
  {
    value: "REIMBURSEMENT_MEDICAL",
    label: "employees.allowanceTypes.reimbursementMedical",
  },
  { value: "HOUSING", label: "employees.allowanceTypes.housing" },
  { value: "NATURA_HOUSING", label: "employees.allowanceTypes.naturaHousing" },
  { value: "PROFESSIONAL", label: "employees.allowanceTypes.professional" },
  { value: "HAZARD", label: "employees.allowanceTypes.hazard" },
  { value: "CERTIFICATION", label: "employees.allowanceTypes.certification" },
  { value: "SHIFT", label: "employees.allowanceTypes.shift" },
  { value: "TRANSPORT", label: "employees.allowanceTypes.transport" },
  { value: "MEAL", label: "employees.allowanceTypes.meal" },
  { value: "OTHER", label: "employees.allowanceTypes.other" },
];

// Allowance types that are the employer's own BPJS contribution rather than
// wage. They ride along as allowances so they land in bruto, but they are not
// upah - counting them would price contributions on contributions.
export const EMPLOYER_CONTRIBUTION_ALLOWANCE_TYPES = [
  "BPJS_KETENAGAKERJAAN_JKK",
  "BPJS_KETENAGAKERJAAN_JKM",
];

// Allowance types that depend on attendance or performance, so they are not
// tunjangan tetap even when paid every month, and stay out of upah - the base
// BPJS is levied on. MEAL and TRANSPORT are left in: they are usually a flat
// monthly amount, and the type alone cannot tell a per-day one apart.
export const VARIABLE_ALLOWANCE_TYPES = [
  "ATTENDANCE",
  "PUNCTUALITY",
  "PERFORMANCE",
  "OVERTIME",
  "SHIFT",
];

export const DEDUCTION_TYPE_OPTIONS = [
  {
    value: "BPJS_KESEHATAN",
    label: "BPJS Kesehatan",
    category: "mandatory",
  },
  {
    value: "BPJS_KETENAGAKERJAAN_JHT",
    label: "BPJS Ketenagakerjaan JHT",
    category: "mandatory",
  },
  {
    value: "BPJS_KETENAGAKERJAAN_JKP",
    label: "BPJS Ketenagakerjaan JKP",
    category: "mandatory",
  },
  {
    value: "BPJS_KETENAGAKERJAAN_JP",
    label: "BPJS Ketenagakerjaan JP",
    category: "mandatory",
  },
  {
    value: "PPH21",
    label: "PPh 21",
    category: "mandatory",
  },
  {
    value: "ACCOMMODATION",
    label: "Akomodasi",
    category: "other",
  },
  {
    value: "INSURANCE_ACCIDENT",
    label: "Asuransi Kecelakaan",
    category: "insurance",
  },
  {
    value: "INSURANCE_HEALTH",
    label: "Asuransi Kesehatan",
    category: "insurance",
  },
  { value: "INSURANCE_LIFE", label: "Asuransi Jiwa", category: "insurance" },
  {
    value: "VEHICLE_INSTALLMENT",
    label: "Cicilan Kendaraan",
    category: "other",
  },
  {
    value: "UNPAID_LEAVE",
    label: "Cuti Tidak Dibayar",
    category: "attendance",
  },
  {
    value: "PENSION_FUND",
    label: "Dana Pensiun",
    category: "benefits",
  },
  {
    value: "LATE_PENALTY",
    label: "Denda Keterlambatan",
    category: "attendance",
  },
  {
    value: "PENSION_CONTRIBUTION",
    label: "Iuran Pensiun",
    category: "tax_deductible",
  },
  {
    value: "UNION_DUES",
    label: "Iuran Serikat Pekerja",
    category: "other",
  },
  {
    value: "CANTEEN",
    label: "Kantin",
    category: "benefits",
  },
  {
    value: "ADVANCE_SALARY",
    label: "Kasbon",
    category: "personal",
  },
  {
    value: "PROFESSIONAL_MEMBERSHIP",
    label: "Keanggotaan Profesi",
    category: "other",
  },
  {
    value: "COOPERATIVE",
    label: "Koperasi",
    category: "benefits",
  },
  {
    value: "PARKING",
    label: "Parkir",
    category: "other",
  },
  {
    value: "LOAN_COOPERATIVE",
    label: "Pinjaman Koperasi",
    category: "personal",
  },
  {
    value: "LOAN_COMPANY",
    label: "Pinjaman Perusahaan",
    category: "personal",
  },
  {
    value: "ABSENCE_DEDUCTION",
    label: "Potongan Ketidakhadiran",
    category: "attendance",
  },
  {
    value: "LIFE_INSURANCE_PREMIUM",
    label: "Premi Asuransi Jiwa",
    category: "tax_deductible",
  },
  {
    value: "UNIFORM",
    label: "Seragam",
    category: "benefits",
  },
  {
    value: "CHARITY",
    label: "Sumbangan",
    category: "other",
  },
  {
    value: "SAVINGS",
    label: "Tabungan",
    category: "other",
  },
  {
    value: "ZAKAT",
    label: "Zakat",
    category: "tax_deductible",
  },
  {
    value: "OTHER",
    label: "Lainnya",
    category: "other",
  },
];

export const PAYMENT_FREQUENCY_OPTIONS = [
  { value: "MONTHLY", label: "employees.frequency.monthly" },
  { value: "ANNUAL", label: "employees.frequency.annual" },
  { value: "ONE_TIME", label: "employees.frequency.oneTime" },
];

export const MARRIAGE_STATUS_OPTIONS = [
  { label: "employees.marriageStatus.single", value: "TK" },
  { label: "employees.marriageStatus.married", value: "K" },
];

export const DEPENDENT_OPTIONS = [
  { label: "0", value: 0 },
  { label: "1", value: 1 },
  { label: "2", value: 2 },
  { label: "3", value: 3 },
];

export const TAX_METHOD_OPTIONS = [
  { label: "employees.taxMethod.gross", value: "GROSS" },
  { label: "employees.taxMethod.grossUp", value: "GROSS_UP" },
];

export const RISK_LEVEL_OPTIONS = [
  { value: "VERY_LOW", label: "employees.riskLevel.veryLow", rate: 0.0024 },
  { value: "LOW", label: "employees.riskLevel.low", rate: 0.0054 },
  { value: "MEDIUM", label: "employees.riskLevel.medium", rate: 0.0089 },
  { value: "HIGH", label: "employees.riskLevel.high", rate: 0.0127 },
  { value: "VERY_HIGH", label: "employees.riskLevel.veryHigh", rate: 0.0174 },
];

export const LATE_DEDUCTION_TYPE_OPTIONS = [
  { value: "HOURLY", label: "employees.lateDeduction.type.hourly" },
  { value: "MINUTE", label: "employees.lateDeduction.type.minute" },
  { value: "FIXED_AMOUNT", label: "employees.lateDeduction.type.fixedAmount" },
  { value: "TIERED", label: "employees.lateDeduction.type.progressive" },
];

export const ATTENDANCE_STATUS_OPTIONS = [
  { value: "PRESENT", label: "employees.attendances.status.present" },
  { value: "ABSENT", label: "employees.attendances.status.absent" },
  { value: "SICK_LEAVE", label: "employees.attendances.status.sickLeave" },
  {
    value: "MEDICAL_LEAVE",
    label: "employees.attendances.status.medicalLeave",
  },
  {
    value: "MATERNITY_LEAVE",
    label: "employees.attendances.status.maternityLeave",
  },
  {
    value: "PATERNITY_LEAVE",
    label: "employees.attendances.status.paternityLeave",
  },
  { value: "ANNUAL_LEAVE", label: "employees.attendances.status.annualLeave" },
  {
    value: "PERSONAL_LEAVE",
    label: "employees.attendances.status.personalLeave",
  },
  {
    value: "EMERGENCY_LEAVE",
    label: "employees.attendances.status.emergencyLeave",
  },
  { value: "UNPAID_LEAVE", label: "employees.attendances.status.unpaidLeave" },
  {
    value: "COMPASSIONATE_LEAVE",
    label: "employees.attendances.status.compassionateLeave",
  },
  { value: "REMOTE_WORK", label: "employees.attendances.status.remoteWork" },
  {
    value: "BUSINESS_TRIP",
    label: "employees.attendances.status.businessTrip",
  },
  { value: "TRAINING", label: "employees.attendances.status.training" },
  { value: "CONFERENCE", label: "employees.attendances.status.conference" },
  {
    value: "CLIENT_MEETING",
    label: "employees.attendances.status.clientMeeting",
  },
  {
    value: "COMPANY_EVENT",
    label: "employees.attendances.status.companyEvent",
  },
];

export const BASIC_ATTENDANCE_STATUS_OPTIONS = [
  { value: "PRESENT", label: "employees.attendances.status.present" },
  { value: "ABSENT", label: "employees.attendances.status.absent" },
  { value: "SICK_LEAVE", label: "employees.attendances.status.sickLeave" },
  { value: "ANNUAL_LEAVE", label: "employees.attendances.status.annualLeave" },
];

export const HOLIDAY_TYPE_OPTIONS = [
  { value: "NATIONAL", label: "employees.holidays.types.national" },
  { value: "COMPANY", label: "employees.holidays.types.company" },
  { value: "REGIONAL", label: "employees.holidays.types.regional" },
];

export const IS_PAID_OPTIONS = [
  { value: true, label: "employees.holidays.isPaid.paid" },
  { value: false, label: "employees.holidays.isPaid.unpaid" },
];
